import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatRupiah, formatApiError } from "@/lib/api";
import SuratDetailDialog from "@/components/SuratDetailDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Save, ArrowLeft, PlusCircle, Layers, ReceiptText, FileDown } from "lucide-react";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function BuatSuratPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    tanggal: todayIso(),
    jenis: "SPH",
    kategori: "Perorangan",
    nama_pemohon: "",
    nama_marketing: "",
    nomor_wa: "",
    nominal: "",
  });
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [justCreated, setJustCreated] = useState(null); // surat object → opens PDF dialog

  const loadPreview = useCallback(async (tanggal, jenis) => {
    try {
      const { data } = await api.get("/settings/numbering/preview", { params: { tanggal, jenis } });
      setPreview(data.nomor_preview);
    } catch (e) {
      // Preview is informational; clear it and log so the issue is traceable.
      console.warn("Preview nomor gagal dimuat:", formatApiError(e));
      setPreview("");
    }
  }, []);

  useEffect(() => {
    loadPreview(form.tanggal, form.jenis);
  }, [form.tanggal, form.jenis, loadPreview]);

  async function onSubmit(e, openPdf = false) {
    e?.preventDefault?.();
    if (!form.nama_pemohon.trim()) return toast.error("Nama pemohon wajib diisi");
    if (!form.nama_marketing.trim()) return toast.error("Nama marketing wajib diisi");
    if (!form.nominal || Number(form.nominal) <= 0) return toast.error("Nominal harus lebih dari 0");
    setLoading(true);
    try {
      const { data } = await api.post("/surat", {
        ...form,
        nominal: Number(form.nominal),
        nama_pemohon: form.nama_pemohon.trim(),
        nama_marketing: form.nama_marketing.trim(),
        nomor_wa: form.nomor_wa.trim() || null,
      });
      toast.success(`Surat berhasil dibuat: ${data.nomor}`);
      if (openPdf) {
        // Buka dialog Detail & PDF langsung di halaman ini.
        setJustCreated(data);
      } else {
        navigate("/surat");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-4xl space-y-6" data-testid="buat-surat-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="-ml-2 text-ink-500" data-testid="btn-back">
        <ArrowLeft className="mr-1 h-4 w-4" /> Kembali
      </Button>
      <PageHeader
        overline="Permohonan baru"
        title="Buat Surat"
        description="Nomor surat akan diterbitkan otomatis setelah Anda menekan tombol simpan."
        icon={PlusCircle}
        tone={form.jenis === "SPH" ? "sph" : "invoice"}
      />

      <Card className="border-border bg-card shadow-none">
        <form onSubmit={onSubmit} className="grid gap-5 p-6 sm:grid-cols-2" data-testid="form-surat">
          <div className="space-y-1">
            <Label>Tanggal</Label>
            <DatePicker
              value={form.tanggal}
              onChange={(v) => setForm({ ...form, tanggal: v })}
              testId="input-tanggal"
              placeholder="Pilih tanggal surat"
            />
          </div>
          <div className="space-y-1">
            <Label>Jenis Surat</Label>
            <Select value={form.jenis} onValueChange={(v) => setForm({ ...form, jenis: v })}>
              <SelectTrigger data-testid="input-jenis"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="SPH">SPH (Surat Penawaran Harga)</SelectItem>
                <SelectItem value="Invoice">Invoice</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Kategori Pemohon</Label>
            <Select value={form.kategori} onValueChange={(v) => setForm({ ...form, kategori: v })}>
              <SelectTrigger data-testid="input-kategori"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Perorangan">Perorangan</SelectItem>
                <SelectItem value="Badan Usaha">Badan Usaha</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Nominal Transaksi (Rp)</Label>
            <Input
              type="number"
              min="0"
              step="1"
              data-testid="input-nominal"
              value={form.nominal}
              onChange={(e) => setForm({ ...form, nominal: e.target.value })}
              placeholder="0"
              required
            />
            {form.nominal ? <p className="mt-1 text-xs text-ink-500">{formatRupiah(form.nominal)}</p> : null}
          </div>

          <div className="sm:col-span-2 space-y-1">
            <Label>{form.kategori === "Perorangan" ? "Nama Pemohon" : "Nama Badan Usaha"}</Label>
            <Input
              data-testid="input-nama-pemohon"
              value={form.nama_pemohon}
              onChange={(e) => setForm({ ...form, nama_pemohon: e.target.value })}
              placeholder={form.kategori === "Perorangan" ? "Contoh: Budi Santoso" : "Contoh: PT Sejahtera Abadi"}
              required
            />
          </div>

          <div className="sm:col-span-2 space-y-1">
            <Label>Nama Marketing</Label>
            <Input
              data-testid="input-nama-marketing"
              value={form.nama_marketing}
              onChange={(e) => setForm({ ...form, nama_marketing: e.target.value })}
              placeholder="Contoh: Andi Pratama"
              required
            />
            <p className="text-xs text-ink-500">Nama marketing yang membawa transaksi ini.</p>
          </div>

          <div className="sm:col-span-2 space-y-1">
            <Label>Nomor WhatsApp Pemohon <span className="text-ink-400">(opsional)</span></Label>
            <Input
              data-testid="input-nomor-wa"
              type="tel"
              inputMode="tel"
              value={form.nomor_wa}
              onChange={(e) => setForm({ ...form, nomor_wa: e.target.value })}
              placeholder="Contoh: 08123456789 atau +6281234567890"
            />
            <p className="text-xs text-ink-500">Hanya Anda &amp; admin yang bisa melihat nomor ini. Nomor yang sama tidak bisa diinput oleh user lain.</p>
          </div>

          <div
            className={`sm:col-span-2 rounded-md border p-4 transition-colors ${
              form.jenis === "SPH"
                ? "border-sky-200 bg-gradient-to-br from-sky-50 to-sky-100/40"
                : "border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100/40"
            }`}
          >
            <p className={`overline ${form.jenis === "SPH" ? "text-sky-800" : "text-amber-800"}`}>
              Preview nomor surat
            </p>
            <p
              className={`mt-2 font-mono text-xl font-semibold tracking-tight ${
                form.jenis === "SPH" ? "text-sky-900" : "text-amber-900"
              }`}
              data-testid="preview-nomor"
            >
              {preview || "—"}
            </p>
            <p className="mt-1 text-xs text-ink-500">Nomor final dapat berubah jika ada surat lain disimpan duluan.</p>
          </div>

          <div className="sm:col-span-2 flex flex-wrap justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate("/surat")} data-testid="btn-cancel">Batal</Button>
            <Button type="submit" disabled={loading} variant="outline" data-testid="btn-submit-surat">
              <Save className="mr-2 h-4 w-4" /> {loading ? "Menyimpan…" : "Simpan saja"}
            </Button>
            <Button
              type="button"
              disabled={loading}
              onClick={(e) => onSubmit(e, true)}
              className="bg-brand text-white hover:bg-brand-600"
              data-testid="btn-submit-and-pdf"
            >
              <FileDown className="mr-2 h-4 w-4" /> {loading ? "Memproses…" : "Simpan & Lanjut Buat PDF"}
            </Button>
          </div>
        </form>
      </Card>

      <SuratDetailDialog
        open={!!justCreated}
        onOpenChange={(v) => { if (!v) { setJustCreated(null); navigate("/surat"); } }}
        surat={justCreated}
        onSaved={() => { /* setelah save, dialog tetap di layar; navigation terjadi saat dialog ditutup */ }}
      />
    </div>
  );
}
